import { Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import ingaziLogo from '@/assets/ingazi-logo.png';
import { ADMIN_PERMISSIONS } from '@/features/admin/admin-tabs';
import { useAuthStore } from '@/lib/auth/auth-store';
import { Can } from '@/lib/rbac/can';
import { roleLabel } from '@/lib/rbac/role-label';
import { cn } from '@/lib/utils';
import { activeNavTo, matchesAudience, matchesTenant, NAV_ITEMS } from './nav-config';

/**
 * The primary navigation rail — a dark indigo column the content sits beside.
 *
 * The rail is dark so the working area reads as the lit surface: navigation is
 * furniture you use a few times a minute, not the thing you look at. The active
 * item is marked twice, by a lifted background AND an amber left edge, because
 * on a dark field a background tint alone is easy to lose at a glance.
 *
 * Items stay permission-filtered through <Can>, using the same effective
 * permissions the backend enforces — the nav never offers a section the user
 * would be 403'd from. Permission alone was not enough, though: a tenant-scoped
 * section also needs a tenant, and a platform Super Admin holds every permission
 * with no tenant at all. Those items are gated on `requiresTenant` too, so the
 * rail cannot offer what the API answers TENANT_REQUIRED to either.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  // Audience is filtered here and permissions inside <Can>, because the two gates
  // have different natures: audience is a plain fact about the account, while a
  // permission check is a hook that must not run conditionally.
  const kind = useAuthStore((s) => s.user?.kind);
  // Same nature as audience — a plain fact about the session, not a hook.
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  // The active section is decided once, by path (not by NavLink's own prefix match), so a page that lives under
  // one section's path but belongs to another — Panel 360 under /awards/… — highlights the section it belongs to.
  const active = activeNavTo(useLocation().pathname);
  return (
    <div className="flex h-full flex-col" style={{ background: 'var(--sidebar)' }}>
      {/* The logo sits ON the indigo as a white card with air around it rather than
          filling a header bar — but the band it sits in is still closed with a
          hairline, as the design has it: the card is a mark, not a nav item, and
          without the rule it reads as the first thing in the list. Same white/8 as
          the account block's divider, so the rail has one hairline value.

          This block is 24 + 52 + 20 = 96px tall, which is `--header-h` — the
          topbar reads that variable so its rule and this one meet as one line.
          Change the padding here and change the variable with it.

          The asset is cropped to its ink, so the height here is the mark's real
          height: 40px of a 52px card. At the old 28px (with the PNG's built-in
          padding) the letters were ~20px and the tagline was unreadable. */}
      <div className="border-b border-white/[0.08] px-5 pb-5 pt-6">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex h-[52px] items-center justify-center overflow-hidden rounded-2xl bg-white px-3"
        >
          <img src={ingaziLogo} alt={/* Ingazi */ t('app.org')} className="block h-10 w-auto max-w-full object-contain" />
        </Link>
      </div>

      <nav aria-label="Primary" className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-4 pt-3">
        {NAV_ITEMS.filter((item) => matchesAudience(item, kind) && matchesTenant(item, tenantId)).map((item) => (
          <Can key={item.to} permission={item.permissions ?? []}>
            <Link
              to={item.to}
              onClick={onNavigate}
              aria-current={active === item.to ? 'page' : undefined}
              className={
                cn(
                  // The amber mark is the pill's own left border, not a bar drawn
                  // over it. A border follows the corner arc: it is at full 4px
                  // through the middle and curves away into the transparent top and
                  // bottom borders, which is the tapered crescent the design shows.
                  // A separate straight bar cannot do that — it runs past the point
                  // where the pill has already curved inward, and reads as a tick
                  // floating outside the pill rather than an edge of it.
                  // Transparent (not absent) on inactive items, so the border box is
                  // the same width in both states and nothing shifts on navigation.
                  'flex h-10 items-center gap-3 rounded-xl border-l-4 border-transparent pl-4 pr-3 text-sm transition-colors duration-150',
                  active === item.to
                    ? 'border-l-ingazi-orange bg-white/[0.12] font-semibold text-white'
                    : 'font-medium text-white/70 hover:bg-white/[0.06] hover:text-white',
                )
              }
            >
              {/* 18px at stroke 1.8, matching the design: lucide's defaults
                  (24px, stroke 2) render visibly heavier beside 14px text. The icon
                  takes the label's colour rather than a separate opacity — in the
                  design the two are the same weight of white. */}
              <item.icon className="size-[18px] shrink-0" strokeWidth={1.8} aria-hidden />
              <span className="flex-1 truncate">{t(item.labelKey)}</span>
            </Link>
          </Can>
        ))}
      </nav>

      <SidebarAccount onNavigate={onNavigate} />
    </div>
  );
}

/** Initials for the avatar — two words at most, "?" rather than an empty circle. */
function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

/**
 * Who you are signed in as, at the foot of the rail.
 *
 * This spot used to hold "New award", which was the wrong thing in two ways:
 * it linked to /awards — the same place the Awards nav item goes, so the most
 * prominent control in the rail duplicated the item directly above it — and it
 * was only ever visible to the minority of staff holding `awards.create`,
 * leaving everyone else with an empty rail foot.
 *
 * An identity block earns the position instead: it is the one thing every
 * signed-in user needs and none of the nav items provide, and on a console where
 * one person can hold several roles across tenants, "who am I acting as right
 * now" is a real question.
 */
function SidebarAccount({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { t } = useTranslation();
  // The store rather than the auth context: <Can> in this same file already
  // reads it, and taking two different routes to one user invites them to
  // disagree. It also keeps the rail renderable without the provider.
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  // The named role is more useful than the account kind: "Organisation admin"
  // tells you what you can do here, "STAFF" tells you almost nothing. It goes
  // through roleLabel because the stored name of a system role is a machine code
  // — printing `user.roles[0]` put "TENANT_ADMIN" under the person's name.
  // Several roles are summarised rather than truncated to the first, which would
  // misrepresent the others as absent.
  //
  // With no roles the two kinds need different words. "Applicant" is a true
  // description of an applicant — they hold no roles by design. For staff it
  // would be a lie: a staff member whose roles were revoked is not an applicant,
  // and labelling them one hides the thing they need to see.
  const label =
    user.roles.length === 1
      ? roleLabel(user.roles[0] as string, t)
      : user.roles.length > 1
        ? /* {{count}} role */ t('nav.rolesCount', { count: user.roles.length })
        : user.kind === 'APPLICANT'
          ? /* Applicant */ t('nav.kind.APPLICANT')
          : /* No roles */ t('nav.noRoles');

  return (
    <div className="border-t border-white/[0.08] px-5 pb-3 pt-4">
      <div className="flex items-center gap-2">
        {/* A circle at 36px, per the design. The gap is tight (8px) for the same
            reason the design's is: the name is the longest string in the rail and
            every pixel spent here is a pixel it loses. */}
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/[0.16] text-[12px] font-semibold text-white"
          aria-hidden
        >
          {initials(user.displayName)}
        </span>
        {/* min-w-0 so the truncation actually happens: without it the flex item
            refuses to shrink below its content and the name overflows the rail. */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white" title={user.displayName}>
            {user.displayName}
          </p>
          {/* Truncated where the design is not: it hardcodes "Admin", but a real
              role name can be long. Identical rendering for anything that fits. */}
          <p className="truncate text-xs text-white/55" title={label}>
            {label}
          </p>
        </div>
      </div>
      {/* A shortcut to the same place the Administration nav item goes. That is
          deliberate duplication and not the flaw the old button had: a small cog
          in the rail's corner is a discoverable affordance, where a full-width
          primary CTA in the rail's most prominent slot competed with the very
          item it repeated. Gated so it is never a dead end.

          Its own line under the identity, as the design has it, rather than a
          third column in that row: a full name is wide enough to want the whole
          rail, and a cog beside it truncates people's names to buy 26px. */}
      <Can permission={ADMIN_PERMISSIONS}>
        <div className="mt-2 flex justify-end">
          <Link
            to="/admin"
            onClick={onNavigate}
            aria-label={/* Settings */ t('nav.admin')}
            title={/* Settings */ t('nav.admin')}
            className="text-white opacity-40 transition-opacity hover:opacity-80"
          >
            <Settings className="size-[18px]" strokeWidth={1.8} aria-hidden />
          </Link>
        </div>
      </Can>
    </div>
  );
}
